import { useState } from "react";
import { Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import {
  Page,
  Table,
  type Column,
  Alert,
  Badge,
  Button,
  Checkbox,
  FormModal,
  FormStack,
  Input,
  Modal,
  Text,
  DateTimeField,
  EmptyState,
} from "@marekl/adminer/components";
import { useConfirm } from "@marekl/adminer/hooks";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifySuccess } from "@marekl/adminer/api";
import { useIsAdmin } from "../../auth/useAuth";
import {
  useCreateWebhook,
  useDeleteWebhook,
  useWebhookDeliveries,
  useWebhookList,
  type Webhook,
  type WebhookDelivery,
  type WebhookEvent,
} from "../../lib/queries/webhooks";
import { TokenRevealModal } from "../../components/TokenRevealModal";
import { JsonBlock } from "../../components/JsonBlock";
import { RefreshButton } from "../../components/RefreshButton";

const EVENT_OPTIONS: { value: WebhookEvent; label: string }[] = [
  { value: "message.sent", label: "Message sent" },
  { value: "message.failed", label: "Message failed" },
  { value: "message.expired", label: "Message expired" },
  { value: "suppression.created", label: "Suppression created" },
  { value: "suppression.deleted", label: "Suppression removed" },
  { value: "batch.completed", label: "Batch completed" },
];

const EVENT_LABELS: Record<string, string> = Object.fromEntries(
  EVENT_OPTIONS.map((o) => [o.value, o.label]),
);

function deliveryPill(state: string) {
  switch (state) {
    case "delivered":
    case "sent":
    case "ok":
      return <Badge weight="ghost" variant="ok" dot>Delivered</Badge>;
    case "failed":
      return <Badge weight="ghost" variant="err" dot>Failed</Badge>;
    case "pending":
      return <Badge weight="ghost" variant="info" dot>Pending</Badge>;
    default:
      return <Badge weight="ghost" variant="muted" dot>{state}</Badge>;
  }
}

interface WebhookFormValues {
  url: string;
  events: Record<WebhookEvent, boolean>;
}

function CreateWebhookModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (secret: string) => void;
}) {
  const create = useCreateWebhook();
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<WebhookFormValues>({
    mode: "onTouched",
    defaultValues: {
      url: "",
      events: {
        "message.sent": false,
        "message.failed": true,
        "message.expired": false,
        "suppression.created": false,
        "suppression.deleted": false,
        "batch.completed": false,
      },
    },
  });

  const eventValues = watch("events");
  const anyEvent = EVENT_OPTIONS.some((o) => eventValues[o.value]);

  async function onSubmit(values: WebhookFormValues) {
    const events = EVENT_OPTIONS.filter((o) => values.events[o.value]).map(
      (o) => o.value,
    );
    try {
      const created = await create.mutateAsync({ url: values.url.trim(), events });
      notifySuccess("The webhook was created.");
      onCreated(created.secret);
    } catch (err) {
      apiErrorToFormErrors<WebhookFormValues>(err, setError, {
        fallbackMessage: "Creation failed. Please try again.",
      });
    }
  }

  return (
    <FormModal
      open
      title="New webhook"
      onClose={onClose}
      busy={create.isPending}
      canSubmit={anyEvent}
      error={rootErrorMessage(errors)}
      submitLabel="Create"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Input
        label="URL"
        type="url"
        required
        placeholder="https://example.com/hooks/letterman"
        spellCheck={false}
        error={errors.url?.message}
        {...register("url", { required: "Required field" })}
      />
      <FormStack>
        {EVENT_OPTIONS.map((o) => (
          <Checkbox
            key={o.value}
            label={o.label}
            {...register(`events.${o.value}` as const)}
          />
        ))}
      </FormStack>
      {!anyEvent && <Alert variant="warn">Select at least one event.</Alert>}
    </FormModal>
  );
}

function DeliveriesModal({
  webhook,
  onClose,
}: {
  webhook: Webhook;
  onClose: () => void;
}) {
  const deliveries = useWebhookDeliveries(webhook.id);

  const columns: Column<WebhookDelivery>[] = [
    {
      key: "created_at",
      header: "Time",
      render: (row) => (
        <Text variant="mono">
          <DateTimeField record={row} source="created_at" />
        </Text>
      ),
    },
    {
      key: "event",
      header: "Event",
      render: (row) => EVENT_LABELS[row.event] ?? row.event,
    },
    {
      key: "state",
      header: "State",
      render: (row) => deliveryPill(row.state),
    },
    {
      key: "attempts",
      header: "Attempts",
      numeric: true,
      render: (row) => <Text variant="mono">{row.attempts}</Text>,
    },
    {
      key: "detail",
      header: "Details",
      render: (row) => (
        <div>
          {row.last_error && (
            <span
              style={{
                fontFamily: "var(--adminer-font-mono, monospace)",
                color: "var(--adminer-color-err)",
                fontSize: "0.85em",
                wordBreak: "break-word",
              }}
            >
              {row.last_error}
            </span>
          )}
          <JsonBlock json={row.payload_json} label="Payload" />
        </div>
      ),
    },
  ];

  return (
    <Modal
      open
      title={`Deliveries: ${webhook.url}`}
      onClose={onClose}
      width={760}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <Table<WebhookDelivery>
        striped
        columns={columns}
        rows={deliveries.data ?? []}
        rowKey={(r) => r.id}
        isLoading={deliveries.isLoading}
        error={deliveries.error}
        onRetry={deliveries.refetch}
        empty={
          <EmptyState
            title="No deliveries"
            description="This webhook has not sent anything yet."
          />
        }
      />
    </Modal>
  );
}

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "reveal"; secret: string }
  | { kind: "deliveries"; webhook: Webhook };

/** Everything this page shows, for the header's refresh button. */
const REFRESH_KEYS = [["webhooks"]];

export function WebhooksPage() {
  const isAdmin = useIsAdmin();
  const { data, isLoading, error, refetch } = useWebhookList();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const confirm = useConfirm();
  const del = useDeleteWebhook();

  function confirmDelete(webhook: Webhook) {
    void confirm({
      title: "Delete webhook",
      description: (
        <>
          Really delete the webhook <strong>{webhook.url}</strong>? Events will
          no longer be sent to it.
        </>
      ),
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: async () => {
        await del.mutateAsync({ id: webhook.id });
      },
    }).then((confirmed) => {
      if (confirmed) notifySuccess("The webhook was deleted.");
    });
  }

  const columns: Column<Webhook>[] = [
    {
      key: "url",
      header: "URL",
      primary: true,
      render: (row) => <Text variant="mono">{row.url}</Text>,
    },
    {
      key: "events",
      header: "Events",
      render: (row) => (
        <>
          {row.events.map((e) => (
            <Badge key={e} variant="info">
              {EVENT_LABELS[e] ?? e}
            </Badge>
          ))}
        </>
      ),
    },
    {
      key: "active",
      header: "Status",
      render: (row) =>
        row.active ? (
          <Badge weight="ghost" variant="ok" dot>Active</Badge>
        ) : (
          <Badge weight="ghost" variant="muted" dot>Inactive</Badge>
        ),
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <Table.Actions>
          <Table.Action onClick={() => setModal({ kind: "deliveries", webhook: row })}>
            Deliveries
          </Table.Action>
          {isAdmin && (
            <Table.Action variant="danger" onClick={() => confirmDelete(row)}>
              Delete
            </Table.Action>
          )}
        </Table.Actions>
      ),
    },
  ];

  return (
    <>
      <Page>
        <Page.Header
          title="Webhooks"
          subtitle="event notifications delivered to your systems"
          subtitleVariant="tagline"
          actions={
            <>
              <RefreshButton queryKeys={REFRESH_KEYS} />
              {isAdmin && (
                <Button variant="primary" onClick={() => setModal({ kind: "create" })}>
                  <Plus size={16} aria-hidden="true" />
                  Add webhook
                </Button>
              )}
            </>
          }
        />
        <Page.Content stack gap="lg">
          <Table<Webhook>
            striped
            cards
            columns={columns}
            rows={data ?? []}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            empty={
              <EmptyState
                title="No webhooks"
                description="Add a webhook to receive events."
              />
            }
          />
        </Page.Content>
      </Page>

      {modal.kind === "create" && (
        <CreateWebhookModal
          onClose={() => setModal({ kind: "closed" })}
          onCreated={(secret) => setModal({ kind: "reveal", secret })}
        />
      )}

      <TokenRevealModal
        open={modal.kind === "reveal"}
        title="Webhook created"
        noun="signing key (HMAC secret)"
        token={modal.kind === "reveal" ? modal.secret : ""}
        onClose={() => setModal({ kind: "closed" })}
      />

      {modal.kind === "deliveries" && (
        <DeliveriesModal
          webhook={modal.webhook}
          onClose={() => setModal({ kind: "closed" })}
        />
      )}
    </>
  );
}
